import Footer from "./Footer";
import { Disclosure } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/20/solid";

const AbsorbSupportInfo = () => {
const generalFaqs = [
    {
        question: "Why are we asking you to register?",
        answer: `Our sponsoring agencies support our efforts to make online training freely available, and they have asked COMET® to collect better usage statistics. The best way to develop this information, and improve our services, is to implement a mandatory registration system. All of us at COMET® appreciate your cooperation and your support.`,
    },
    {
        question: "Is MetEd secure?",
        answer: (
            <>
                We use Absorb Software Inc. as our Learning Management System provider. For more information on how they secure data see <a href="https://www.absorblms.com/support/compliance/soc-2/" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">here</a>.
            </>
        ),
    },
    {
        question: "What will you do with my information?",
        answer: `In the interest of privacy, we ask for a minimal amount of required information: first and last name, e-mail address, password, organizational affiliation, and the country in which you live. This information is used for account management, user support, and to generate anonymous statistical reports about our user base. User data will be reported to COMET sponsors only on an aggregate basis with no personal, individual information identified, unless you have given us your explicit permission by checking “Allow my organization to view my results” during registration. This permission allows your organization to access your course completions and quiz scores.`,
    },
    {
        question: "Is this service free?",
        answer: `Yes. You are required to register to use our materials but there is no cost involved and it is a very simple process. Once you have registered you have access to our entire content library.`,
    },
    {
        question: "Can I receive college credit for COMET courses?",
        answer: `COMET® MetEd does not offer any college courses or credit of any kind. Our courses are used widely in university courses, and some organizations, such as the American Meteorological Society, do give continuing education credits for completion of courses; however, none of that is administered through our program.`,
    },    
    
];

const accountFaqs = [
    {
        question: "I forgot my password",
        answer: (
            <>
                To request a new password, please click on the ”Forgot password?" link found below the login button. Your Username is the email address you used to register. MetEd no longer supports using a username that differs from your email address.
            </>
        ),
    },
    {
        question: "I didn’t receive an email to verify my account",
        answer: (
            <>
            If you didn’t receive an expected email (such as account verification), please check your spam or junk folder — it may have been filtered there. These emails are sent from noreply@myabsorb.com. To avoid this in the future:
            <ul className="list-disc pl-6">
                <li>Add <strong>noreply@myabsorb.com</strong> to your email contacts or safe sender list.</li>
                <li>If you’re using a work or institutional email, ask your IT department to whitelist that address.</li>
            </ul>
            Still can’t find it? Contact us at <a href="mailto:lms_support@meted.ucar.edu" className="text-blue-600 underline">lms_support@meted.ucar.edu</a> for assistance.
            </>
        ),
    },
    {
        question: "Can I change my organizational affiliation after registering?",
        answer: (
            <>
                We no longer support changing your organizational affiliation after initial registration. If you need to use a different affiliation, you will need to create a new account using a different email address. Please note: accounts cannot be merged and will remain separate.
            </>
        ),
    }
];

const usingMetedFaqs = [
    {
        question: "I can't find a printable version of the course",
        answer: `We no longer support a downloadable version of our courses or a printable version of the course content.`,
    },
];

const quizFaqs = [
    {
        question: "Sharing quiz scores",
        answer: (
            <>
                Quiz scores will be provided in your course completion emails and can be shared with whomever you like. This includes your instructor/supervisor as they will no longer receive automatic scores like they did on the legacy platform.                
            </>
        ),
    },
    {
        question: "I left the quiz before it was completed",
        answer: (
            <>
                All answers will be recorded as you complete each question, however, when you reenter the quiz the questions may be in a different order. The saved questions will have a check mark next to them.
            </>
        ),
    }
];

const certificateFaqs = [
    {
        question: "How does MetEd define a completed course?",
        answer: (
            <>
                When you have successfully completed the basic required sections of the course: the course content, the quiz, and the survey.
            </>
        ),
    },
    {
        question: "How do I find my certificate?",
        answer: (
            <>
                <ul className="list-disc pl-6">
                    <li>Login to MetEd and go to “Transcript”</li>
                    <li>Find the course you completed and click the “Download” button next to it</li>
                    <li>It is downloadable as a PDF and can be printed or shared</li>
                    <li><strong>Note:</strong> If there's no download button, the course isn't fully complete — return to the course and finish all required sections.</li>
                </ul>
            </>
        ),
    },
    {
        question: "I can't print my certificate",
        answer: (
            <>
                Certificates are made available as PDF files. You will need to have a PDF viewer installed in order to view and print certificates.
            </>
        ),
    }
];

const subscriptionsFaqs = [
    {
        question: "How can I unsubscribe from your email list?",
        answer: `Click the "Unsubscribe" link at the bottom of any email from us.`,
    },
];

const contactUsFaqs = [
    {
        question: "How can I contact you?",
        answer: (
            <>
                <div className="bg-yellow-100 p-4 mb-4">
                <p className="font-bold">If you are having trouble logging in:</p>
                <p>Please note that MetEd transitioned to a new platform on June 2, 2025. Login credentials from the previous system are no longer valid. To continue on the new platform, create a new account here: <a href="https://learn.meted.ucar.edu/#/public-dashboard" className="text-blue-600 underline">https://learn.meted.ucar.edu/#/public-dashboard</a>.</p>
                <br />
                <p className="font-bold"> If you need your legacy records:</p>
                <p>You can still download your transcript, certificates, and course queue from the previous platform here: <a href="https://www.meted.ucar.edu/education_training/transcripts" className="text-blue-600 underline">https://www.meted.ucar.edu/education_training/transcripts</a>.</p>
                <br />
                <p className="font-bold">For support questions:</p>
                <p>E-mail us at <a href="mailto:lms_support@meted.ucar.edu" className="text-blue-600 underline">lms_support@meted.ucar.edu</a>.</p>
                <br />
                <p className="font-bold">For information on using our visuals (graphics, videos, etc.):</p>
                <p>Please refer to our <a href="https://www.meted.ucar.edu/education_training/legal" className="text-blue-600 underline">Site Terms of Use</a>.</p>
                </div>
            </>
        ),
    },
];

return (
    <div>
        <main className="mb-24">
            <div className="relative py-24 sm:py-20">
                <div className="mx-auto max-w-7xl px-3 lg:px-8">
                    <div className="mx-auto text-center">
                        <h1 className="text-4xl font-bold tracking-tight text-gray-900">
                            Support & Frequently Asked Questions
                        </h1>
                    </div>
                </div>
            </div>
            <div className="mx-auto max-w-7xl px-4">
                <h3 className="text-2xl font-semibold text-gray-700 mb-6 border-t border-gray-200 pt-4">
                    General Information
                </h3>
                <div className="space-y-4 mb-4">
                    <Disclosure defaultOpen={true}>
                        <Disclosure.Button className="flex w-full justify-between rounded-lg bg-[#1a658f] px-4 py-3 text-left text-lg font-medium text-white hover:bg-[#007fa3] focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                            <span>Legacy vs. Current Terminology</span>
                            <ChevronUpIcon className="h-8 w-8 text-white rotate-180 transform"
                            />
                        </Disclosure.Button>
                        <Disclosure.Panel className="px-4 pb-4 pt-2 text-gray-700 text-base">
                            MetEd recently updated the terminology on its platform to better reflect the structure and purpose of its educational content:
                                <table className="divide-y divide-gray-200 mx-auto my-4 w-full max-w-2xl text-center">
                                    <thead>
                                        <tr>
                                            <th className="px-6 py-4 text-left text-sm font-bold text-gray-900 outline outline-1 outline-gray-200">Legacy Terminology</th>
                                            <th className="px-6 py-4 text-left text-sm font-bold text-gray-900 outline outline-1 outline-gray-200">Current Terminology</th>
                                            <th className="px-6 py-4 text-left text-sm font-bold text-gray-900 outline outline-1 outline-gray-200">Definition</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 outline outline-1 outline-gray-200 text-left">
                                        <tr>
                                            <td className="whitespace-nowrap px-6 py-2 text-sm text-gray-700 border border-gray-200">Lesson</td>
                                            <td className="whitespace-nowrap px-6 py-2 text-sm text-gray-700 border border-gray-200">Course</td>
                                            <td className="whitespace-wrap px-6 py-2 text-sm text-gray-700 border border-gray-200">Targeted content on one focused subject</td>
                                        </tr>
                                        <tr>
                                            <td className="whitespace-nowrap px-6 py-2 text-sm text-gray-700 border border-gray-200">Course</td>
                                            <td className="whitespace-nowrap px-6 py-2 text-sm text-gray-700 border border-gray-200">Curriculum</td>
                                            <td className="whitespace-wrap px-6 py-2 text-sm text-gray-700 border border-gray-200">A collection of courses centered around a broader theme/topic</td>
                                        </tr>
                                    </tbody>
                                </table>
                        </Disclosure.Panel>
                    </Disclosure>
                </div>
                <div className="space-y-4">
                    {generalFaqs.map((faq, idx) => (
                        <Disclosure key={idx}>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="flex w-full justify-between rounded-lg bg-[#1a658f] px-4 py-3 text-left text-lg font-medium text-white hover:bg-[#007fa3] focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                                        <span>{faq.question}</span>
                                        <ChevronUpIcon
                                            className={`${
                                                open ? "rotate-180 transform" : ""
                                            } h-8 w-8 text-white`}
                                        />
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="px-4 pb-4 pt-2 text-gray-700 text-base">
                                        {faq.answer}
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    ))}
                </div>

                <h3 className="text-2xl font-semibold text-gray-700 my-6 border-t border-gray-200 pt-4">
                    Login and User Account Issues
                </h3>
                <div className="space-y-4">
                    {accountFaqs.map((faq, idx) => (
                        <Disclosure key={idx}>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="flex w-full justify-between rounded-lg bg-[#1a658f] px-4 py-3 text-left text-lg font-medium text-white hover:bg-[#007fa3] focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                                        <span>{faq.question}</span>
                                        <ChevronUpIcon
                                            className={`${
                                                open ? "rotate-180 transform" : ""
                                            } h-8 w-8 text-white`}
                                        />
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="px-4 pb-4 pt-2 text-gray-700 text-base">
                                        {faq.answer}
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    ))}
                </div>

                <h3 className="text-2xl font-semibold text-gray-700 my-6 border-t border-gray-200 pt-4">
                    Using MetEd Courses
                </h3>
                <div className="space-y-4">
                    {usingMetedFaqs.map((faq, idx) => (
                        <Disclosure key={idx}>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="flex w-full justify-between rounded-lg bg-[#1a658f] px-4 py-3 text-left text-lg font-medium text-white hover:bg-[#007fa3] focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                                        <span>{faq.question}</span>
                                        <ChevronUpIcon
                                            className={`${
                                                open ? "rotate-180 transform" : ""
                                            } h-8 w-8 text-white`}
                                        />
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="px-4 pb-4 pt-2 text-gray-700 text-base">
                                        {faq.answer}
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    ))}
                </div>

                <h3 className="text-2xl font-semibold text-gray-700 my-6 border-t border-gray-200 pt-4">
                    Quiz Problems
                </h3>
                <p className="mb-2 bg-yellow-100 p-2"><strong>Note:</strong> Retaking a quiz will erase your previous score ONLY if you receive a higher score.</p>
                <div className="space-y-4">
                    {quizFaqs.map((faq, idx) => (
                        <Disclosure key={idx}>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="flex w-full justify-between rounded-lg bg-[#1a658f] px-4 py-3 text-left text-lg font-medium text-white hover:bg-[#007fa3] focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                                        <span>{faq.question}</span>
                                        <ChevronUpIcon
                                            className={`${
                                                open ? "rotate-180 transform" : ""
                                            } h-8 w-8 text-white`}
                                        />
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="px-4 pb-4 pt-2 text-gray-700 text-base">
                                        {faq.answer}
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    ))}
                </div>

                <h3 className="text-2xl font-semibold text-gray-700 my-6 border-t border-gray-200 pt-4">
                    Certificate Issues
                </h3>
                <div className="space-y-4">
                    {certificateFaqs.map((faq, idx) => (
                        <Disclosure key={idx}>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="flex w-full justify-between rounded-lg bg-[#1a658f] px-4 py-3 text-left text-lg font-medium text-white hover:bg-[#007fa3] focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                                        <span>{faq.question}</span>
                                        <ChevronUpIcon
                                            className={`${
                                                open ? "rotate-180 transform" : ""
                                            } h-8 w-8 text-white`}
                                        />
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="px-4 pb-4 pt-2 text-gray-700 text-base">
                                        {faq.answer}
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    ))}
                </div>

                <h3 className="text-2xl font-semibold text-gray-700 my-6 border-t border-gray-200 pt-4">
                    Subscriptions
                </h3>
                <div className="space-y-4">
                    {subscriptionsFaqs.map((faq, idx) => (
                        <Disclosure key={idx}>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="flex w-full justify-between rounded-lg bg-[#1a658f] px-4 py-3 text-left text-lg font-medium text-white hover:bg-[#007fa3] focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                                        <span>{faq.question}</span>
                                        <ChevronUpIcon
                                            className={`${
                                                open ? "rotate-180 transform" : ""
                                            } h-8 w-8 text-white`}
                                        />
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="px-4 pb-4 pt-2 text-gray-700 text-base">
                                        {faq.answer}
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    ))}
                </div>

                <h3 className="text-2xl font-semibold text-gray-700 my-6 border-t border-gray-200 pt-4">
                    Contact Us
                </h3>
                <div className="space-y-4">
                    {contactUsFaqs.map((faq, idx) => (
                        <Disclosure key={idx} defaultOpen={true}>
                            {({ open }) => (
                                <>
                                    <Disclosure.Button className="flex w-full justify-between rounded-lg bg-[#1a658f] px-4 py-3 text-left text-lg font-medium text-white hover:bg-[#007fa3] focus:outline-none focus-visible:ring focus-visible:ring-blue-500/75">
                                        <span>{faq.question}</span>
                                        <ChevronUpIcon
                                            className={`${
                                                open ? "rotate-180 transform" : ""
                                            } h-8 w-8 text-white`}
                                        />
                                    </Disclosure.Button>
                                    <Disclosure.Panel className="px-4 pb-4 pt-2 text-gray-700 text-base">
                                        {faq.answer}
                                    </Disclosure.Panel>
                                </>
                            )}
                        </Disclosure>
                    ))}
                </div>
            </div>
        </main>
        <Footer />
    </div>
);
};

export default AbsorbSupportInfo;
